import React, { useRef, useState } from "react";
import styled from "styled-components";

import { HSpace, SwitchButton } from "@examspark/common-ui";
import { IconClockLine } from "@examspark/common-ui/dist/components/icons";
import {
  ExamBlobs,
  ExamConfigData,
  ExamScore,
  ExamSessionMetadata,
} from "@examspark/types";

import { useIntersectionObserver } from "../../../hooks/useIntersectionObserver";

const Styled = styled.div`
  border: solid 1px #ddd;
  border-radius: 0.5rem;
  position: relative;
  overflow: hidden;
`;
const ItemNumber = styled.div`
  color: #999;
  font-size: 2.5rem;
  flex: 1;
  font-weight: lighter;
`;

const ItemScore = styled.div`
  font-size: 2rem;

  span {
    font-weight: lighter;
  }
`;
const ItemIcon = styled.div`
  position: absolute;
  left: -4rem;
  top: 1.5rem;
`;

const CorrectAnswerWrapper = styled.div``;

const CorrectAnswer = styled.div`
  border-top: solid 1px #ddd;
  min-height: 20rem;
  position: relative;
`;

const CorrectAnswerToolbar = styled.div`
  padding: 1rem;
  border-top: solid 1px #ddd;
`;

const Unscored = styled(HSpace)`
  background-color: #f2f2f2;
  padding: 1rem;
  color: #333;
  align-items: center;

  svg {
    fill: #666;
    display: block;
  }
`;

/**
 * Item overlay to prevent student from interacting with question. Better
 * solution ultimately is to update question player, but this gets us pretty
 * close to same outcome. */
const StyledDisableItemUIShim = styled.div`
  width: 100%;
  height: 100%;
  position: absolute;
  top: 0;
`;

const ResultItem = (props: {
  item: ExamScore["items"][0];
  itemPreviewer?: any;
  idx: number;
  config?: ExamConfigData;
  sessionData?: ExamSessionMetadata;
  response?: any;
  blobs?: ExamBlobs[string];
}) => {
  const { item, idx } = props;
  const ref = useRef<HTMLDivElement | null>(null);
  const [showCorrectAnswer, setShowCorrectAnswer] = useState(false);
  const entry = useIntersectionObserver(ref, {
    freezeOnceVisible: true,
  });
  const isVisible = !!entry?.isIntersecting;

  const iconCaution = (
    <svg viewBox="0 0 511.998 511.998" width="20" fill="orange">
      <g>
        <g>
          <path d="M508.057,444.469L284.035,37.015c-5.693-10.342-16.179-16.527-28.036-16.527c-11.857,0-22.343,6.185-28.036,16.527    L3.941,444.469c-5.427,9.932-5.243,21.646,0.512,31.354c5.796,9.83,16.097,15.687,27.544,15.687H480    c11.448,0,21.749-5.857,27.545-15.687C513.299,466.115,513.484,454.401,508.057,444.469z M250.941,184.752    c1.72-0.287,3.42-0.43,5.099-0.43c14.745,0,27.749,10.547,30.248,25.394c0.594,3.379,0.532,6.82,0,10.014l-20.193,119.947    c-0.84,4.956-5.181,8.478-10.076,8.478c-0.553,0-1.147-0.041-1.7-0.143c-4.444-0.737-7.721-4.178-8.417-8.335L225.71,219.73    C222.905,203.163,234.23,187.496,250.941,184.752z M255.999,430.072c-16.936,0-30.719-13.782-30.719-30.719    s13.782-30.719,30.719-30.719s30.719,13.783,30.719,30.719S272.935,430.072,255.999,430.072z"></path>
        </g>
      </g>
    </svg>
  );

  const iconCorrect = (
    <svg viewBox="0 0 408.576 408.576" width="20" fill="green">
      <g>
        <g>
          <path d="M204.288,0C91.648,0,0,91.648,0,204.288s91.648,204.288,204.288,204.288s204.288-91.648,204.288-204.288    S316.928,0,204.288,0z M318.464,150.528l-130.56,129.536c-7.68,7.68-19.968,8.192-28.16,0.512L90.624,217.6    c-8.192-7.68-8.704-20.48-1.536-28.672c7.68-8.192,20.48-8.704,28.672-1.024l54.784,50.176L289.28,121.344    c8.192-8.192,20.992-8.192,29.184,0C326.656,129.536,326.656,142.336,318.464,150.528z"></path>
        </g>
      </g>
    </svg>
  );

  const iconIncorrect = (
    <svg viewBox="0 0 512 512" width="20" fill="#d92f2f">
      <path d="m437.019531 74.980469c-48.351562-48.351563-112.640625-74.980469-181.019531-74.980469s-132.667969 26.628906-181.019531 74.980469c-48.351563 48.351562-74.980469 112.640625-74.980469 181.019531 0 68.382812 26.628906 132.667969 74.980469 181.019531 48.351562 48.351563 112.640625 74.980469 181.019531 74.980469s132.667969-26.628906 181.019531-74.980469c48.351563-48.351562 74.980469-112.636719 74.980469-181.019531 0-68.378906-26.628906-132.667969-74.980469-181.019531zm-70.292969 256.386719c9.761719 9.765624 9.761719 25.59375 0 35.355468-4.882812 4.882813-11.28125 7.324219-17.679687 7.324219s-12.796875-2.441406-17.679687-7.324219l-75.367188-75.367187-75.367188 75.371093c-4.882812 4.878907-11.28125 7.320313-17.679687 7.320313s-12.796875-2.441406-17.679687-7.320313c-9.761719-9.765624-9.761719-25.59375 0-35.355468l75.371093-75.371094-75.371093-75.367188c-9.761719-9.765624-9.761719-25.59375 0-35.355468 9.765624-9.765625 25.59375-9.765625 35.355468 0l75.371094 75.367187 75.367188-75.367187c9.765624-9.761719 25.59375-9.765625 35.355468 0 9.765625 9.761718 9.765625 25.589844 0 35.355468l-75.367187 75.367188zm0 0"></path>
    </svg>
  );

  if (!item.score) return null;
  let icon: any;
  if (!item.score?.actual) {
    icon = iconIncorrect;
  } else if (item.score?.actual === item.score?.max) {
    icon = iconCorrect;
  } else if (item.score?.actual > 0) {
    icon = iconCaution;
  } else {
    icon = iconIncorrect;
  }

  const ItemPreviewer = props.itemPreviewer;

  const showCorrectAnswers = props.config?.showCorrectAnswersOnComplete;
  const showResponses = props.config?.showResponsesOnComplete;

  return (
    <Styled>
      <ItemIcon>{icon}</ItemIcon>
      <div>
        {item.score?.actual == null && (
          <Unscored>
            <div>
              <IconClockLine width="16" />
            </div>
            <div>This question is awaiting a score from an instructor.</div>
          </Unscored>
        )}
        <HSpace style={{ alignItems: "center", padding: "1rem" }}>
          <ItemNumber>Question {String(idx + 1)}</ItemNumber>
          {item.score.actual != null && (
            <ItemScore>
              <span>Points:</span> {item.score?.actual || "0"} /{" "}
              {item.score?.max || "0"}
            </ItemScore>
          )}
        </HSpace>
        {(showCorrectAnswers || showResponses) &&
          props.itemPreviewer != null && (
            <CorrectAnswerWrapper ref={ref}>
              {showCorrectAnswers && (
                <CorrectAnswerToolbar>
                  <SwitchButton
                    label="Reveal Answer"
                    checked={showCorrectAnswer}
                    onClick={() => setShowCorrectAnswer(!showCorrectAnswer)}
                  />
                </CorrectAnswerToolbar>
              )}
              <CorrectAnswer>
                {isVisible && (
                  <ItemPreviewer
                    itemId={item.id}
                    condensed
                    sessionData={props.sessionData}
                    showKey={showCorrectAnswer}
                    response={props.response}
                    blobs={props.blobs}
                  />
                )}
                <StyledDisableItemUIShim />
              </CorrectAnswer>
            </CorrectAnswerWrapper>
          )}
      </div>
    </Styled>
  );
};

export default ResultItem;
